import React, { useState } from 'react';
import { Spin } from 'antd';
import Swal from 'sweetalert2';
import { OrderFormComponent } from './OrderFormComponent';
import { useDishCategories } from '../../../../../core-module/hooks/useDishCategories';
import Form, { FormComponentProps } from 'antd/lib/form';
import {
  findDeliveryInfoByDeliveryId,
  getPlaceOrderValidationResult,
  getPlaceOrderRequest,
  hasMealWithMoreThanThreeDishes,
  getDeliveryMoments,
  getSelectedDeliveryMoment
} from '../../../../../core-module/selectors/Order/selectors';
import { logisticsClient } from '../../../../../core-module/services/logistics/LogisticsClient';
import { FormValues } from '../../../../../core-module/selectors/Order/types';
import {
  PlaceOrderRequest,
  OrderHistoryResponse,
  OrderHistoryItem
} from '../../../../../core-module/services/logistics/types';
import { DeliveryInfoResponse } from '../../../../../core-module/services/website/types';

interface OwnProps {
  readonly customerId: number;
  readonly deliveryInfo: DeliveryInfoResponse;
  readonly orderHistory: OrderHistoryResponse;
  readonly customerDefaultPortionId: number | null;
  readonly onHideClick: () => void;
  readonly existingOrder?: OrderHistoryItem;
}

interface SelectedDeliveryMomentState {
  selectedDeliveryId: number;
  selectedDeliveryMomentId: number;
}

type Props = OwnProps & FormComponentProps<FormValues>;

const OrderFormContainer: React.FC<Props> = ({
  customerId,
  deliveryInfo,
  orderHistory,
  customerDefaultPortionId,
  onHideClick,
  existingOrder,
  form
}) => {
  const categoryQuery = useDishCategories();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selected, setSelected] = useState<SelectedDeliveryMomentState | null>(
    existingOrder
      ? {
          selectedDeliveryId: existingOrder.delivery_id,
          selectedDeliveryMomentId: existingOrder.delivery_moment_id
        }
      : null
  );

  const handleSave = () => {
    form.validateFields(['route_comments', 'dishes', 'voucher_code'], (errors, values) => {
      const selectedDeliveryInfo = findDeliveryInfoByDeliveryId(
        deliveryInfo,
        selected!.selectedDeliveryId
      );

      const validationResult = getPlaceOrderValidationResult(selectedDeliveryInfo, values);

      if (!validationResult.isValid) {
        Swal({
          type: 'warning',
          confirmButtonText: 'Ok',
          title: 'Let op',
          html: validationResult.message
        });

        return;
      }

      const placeOrderRequest = getPlaceOrderRequest(
        customerId,
        selectedDeliveryMoment!,
        values,
        existingOrder
      );
      const showLargeQuantityWarning = hasMealWithMoreThanThreeDishes(placeOrderRequest);

      if (showLargeQuantityWarning) {
        Swal({
          title: 'Let op',
          html: `Voor 1 of meer maaltijden is een aantal groter dan 3 ingevoerd. Weet u zeker dat u door wilt gaan?`,
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Ja',
          cancelButtonText: 'Nee'
        }).then((result) => {
          if (!result.dismiss) {
            placeOrder(placeOrderRequest);
          }
        });
      } else {
        placeOrder(placeOrderRequest);
      }
    });
  };

  const placeOrder = (placeOrderRequest: PlaceOrderRequest) => {
    setIsSubmitting(true);
    logisticsClient
      .placeOrder(placeOrderRequest)
      .then(() => {
        Swal({
          type: 'success',
          confirmButtonText: 'Ok',
          title: 'Bestelling geplaatst',
          text: 'De bestelling is succesvol geplaatst'
        });
        onHideClick();
      })
      .catch((err) => {
        Swal({
          type: 'error',
          confirmButtonText: 'Ok',
          title: 'Er is een fout opgetreden',
          text: err.message
        });
      })
      .finally(() => setIsSubmitting(false));
  };

  const handleChangeDeliveryMoment = (
    selectedDeliveryId: number,
    selectedDeliveryMomentId: number
  ) => {
    setSelected({ selectedDeliveryId, selectedDeliveryMomentId });
  };

  if (categoryQuery.loading) {
    return <Spin />;
  }

  if (categoryQuery.error) {
    return <p>Er is een fout opgetreden bij het ophalen van maaltijdcategorieën.</p>;
  }

  const selectableDeliveryMoments = getDeliveryMoments(
    categoryQuery.categories,
    deliveryInfo,
    orderHistory
  );

  const selectedDeliveryMoment = selected
    ? getSelectedDeliveryMoment(
        selectableDeliveryMoments,
        selected.selectedDeliveryId,
        selected.selectedDeliveryMomentId
      )
    : null;

  return (
    <OrderFormComponent
      form={form}
      selectableDeliveryMoments={selectableDeliveryMoments}
      selectedDeliveryMoment={selectedDeliveryMoment || null}
      isSubmitting={isSubmitting}
      existingOrder={existingOrder}
      customerDefaultPortionId={customerDefaultPortionId}
      onSaveClick={handleSave}
      onHideClick={onHideClick}
      onChangeDeliveryMoment={handleChangeDeliveryMoment}
    />
  );
};

export default Form.create<Props>()(OrderFormContainer);
